import * as React from 'react'
import Box from '@mui/material/Box'
import { isSameDay } from 'date-fns'
import type { CalendarEvent } from '../../typings'
import { useToday } from '../../hooks/useToday'
import { AllDayChips } from './AllDayChips'
import { HourGutter } from './HourGutter'
import { TimedEventsColumn } from './TimedEventsColumn'
import { STICKY_HEADER_SX } from './stickyHeader'

type Props = {
  day: Date
  allDayEvents: CalendarEvent[]
  timedEvents: CalendarEvent[]
}

// The day column is wide, so it tolerates more lanes than a week column; beyond
// that the Up-next rail beside it lists every event in full.
const DAY_MAX_LANES = 6

/**
 * The day view's single positioned column: an all-day strip above, then an hour
 * gutter beside one lane-packed column of timed tiles. The gutter sits in the
 * timed row only, so its labels stay aligned with the gridlines even when all-day
 * chips push the strip taller.
 */
export function DayColumn({ day, allDayEvents, timedEvents }: Props) {
  const today = useToday()

  return (
    <Box
      data-test-id="day-column"
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '48px minmax(0, 1fr)',
          sm: '64px minmax(0, 1fr)',
        },
      }}
    >
      <Box aria-hidden sx={STICKY_HEADER_SX} />
      <Box
        sx={{
          ...STICKY_HEADER_SX,
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          py: '4px',
        }}
      >
        <AllDayChips events={allDayEvents} day={day} />
      </Box>

      <HourGutter />

      <TimedEventsColumn
        testId="day-timed-column"
        day={day}
        events={timedEvents}
        maxLanes={DAY_MAX_LANES}
        highlightToday={isSameDay(day, today)}
      />
    </Box>
  )
}
